import { useState } from 'react';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import type { ClientId, SessionChargeId } from '@nesso/ledger-contracts/ids';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@nesso/ui/primitives/tabs';

import { type Payment, paymentListOptions } from '@/entities/payment';
import { formatServiceLabel, serviceCatalogOptions } from '@/entities/service-catalog';
import {
    clientBalanceOptions,
    type SessionCharge,
    sessionChargeListOptions,
} from '@/entities/session-charge';
import { EditSessionFeeDialog } from '@/features/ledger/edit-session-fee';
import { RecordPaymentSheet } from '@/features/ledger/record-payment';
import { RefundPaymentSheet } from '@/features/ledger/refund-payment';
import { useSheetStore } from '@/shared/stores/sheet-store';
import { CenteredSpinner } from '@/shared/ui/indicators/centered-spinner';
import { SheetBreadcrumb } from '@/shared/ui/panels/sheet-breadcrumb';
import {
    SheetClose,
    SheetCloseButton,
    SheetDescription,
    SheetHeader,
    SheetPanel,
    SheetTitle,
} from '@/shared/ui/panels/sheet-panel';

import {
    type ActivityFilterValue,
    CLIENT_BILLING_ACTIVITY_FILTER_OPTIONS,
} from '../config/client-billing-activity-filter';
import { ClientBillingSheetActivity } from './client-billing-sheet/client-billing-sheet-activity';
import { ClientBillingSheetAppliedToDialog } from './client-billing-sheet/client-billing-sheet-applied-to-dialog';
import { ClientBillingSheetDocuments } from './client-billing-sheet/client-billing-sheet-documents';
import { ClientBillingSheetSessions } from './client-billing-sheet/client-billing-sheet-sessions';
import { ClientBillingSheetSettings } from './client-billing-sheet/client-billing-sheet-settings';
import { ClientBillingSheetSummary } from './client-billing-sheet/client-billing-sheet-summary';

export function ClientBillingSheet() {
    const currentSheet = useSheetStore((state) => state.currentSheet);
    const isOpen = currentSheet?.type === 'clients/billing';
    const [lastClient, setLastClient] = useState(isOpen ? currentSheet.data : undefined);
    // Radix keeps the content mounted through the slide-out, after the store has already moved on.
    if (isOpen && currentSheet.data !== lastClient) setLastClient(currentSheet.data);
    const client = isOpen ? currentSheet.data : lastClient;
    const balanceQuery = useQuery({
        ...clientBalanceOptions(client?.id ?? ''),
        enabled: isOpen,
    });
    const sessionChargeListQuery = useInfiniteQuery({
        ...sessionChargeListOptions(client?.id ?? ''),
        enabled: isOpen,
    });
    const isBillingLoading = balanceQuery.isLoading || sessionChargeListQuery.isLoading;
    const [hasOpenedActivity, setHasOpenedActivity] = useState(false);
    const [activityFilter, setActivityFilter] = useState<ActivityFilterValue>('all');
    const activityFilterOption = CLIENT_BILLING_ACTIVITY_FILTER_OPTIONS.find(
        (option) => option.value === activityFilter,
    );
    // Write-offs has no `kind` of its own to request — it sends nothing at all, so its cache
    // key must not collide with All's (both would otherwise read `kind: undefined`).
    const isWriteOffsFilter = activityFilterOption?.kind === null;
    const paymentListQuery = useInfiniteQuery({
        ...paymentListOptions(client?.id ?? '', activityFilterOption?.kind ?? undefined),
        enabled: isOpen && hasOpenedActivity && !isWriteOffsFilter,
    });
    const { data: catalog } = useQuery(serviceCatalogOptions());
    const [appliedTo, setAppliedTo] = useState<{
        payment: Payment;
        highlightedSessionChargeId: string | null;
    } | null>(null);
    const [recordPayment, setRecordPayment] = useState<{
        open: boolean;
        selectedSessionChargeId: SessionChargeId | null;
    }>({ open: false, selectedSessionChargeId: null });
    const [refundPayment, setRefundPayment] = useState<{
        open: boolean;
        payment: Payment | null;
    }>({ open: false, payment: null });
    const [editSessionFee, setEditSessionFee] = useState<{
        open: boolean;
        sessionCharge: SessionCharge | null;
    }>({ open: false, sessionCharge: null });
    if (!isOpen && appliedTo) setAppliedTo(null);
    if (!isOpen && hasOpenedActivity) setHasOpenedActivity(false);
    if (!isOpen && activityFilter !== 'all') setActivityFilter('all');

    function getServiceLabel(serviceCode: string) {
        const description = catalog?.services.find(
            (service) => service.code === serviceCode,
        )?.description;

        return formatServiceLabel(serviceCode, description);
    }

    function openAppliedToFromActivity(paymentId: string, sessionChargeId: string) {
        const payment = paymentListQuery.data?.pages
            .flatMap((paymentListPage) => paymentListPage.items)
            .find(
                (candidate): candidate is Payment & { kind: 'PAYMENT' } =>
                    candidate.kind === 'PAYMENT' && candidate.id === paymentId,
            );
        if (payment) setAppliedTo({ payment, highlightedSessionChargeId: sessionChargeId });
    }

    function openAppliedToFromTransaction(payment: Payment, sessionChargeId: string) {
        setAppliedTo({ payment, highlightedSessionChargeId: sessionChargeId });
    }

    function openAppliedToFromRefund(payment: Payment) {
        setAppliedTo({ payment, highlightedSessionChargeId: null });
    }

    function openRecordPayment(selectedSessionChargeId: SessionChargeId | null) {
        setRecordPayment({ open: true, selectedSessionChargeId });
    }

    function openRefund(payment: Payment) {
        setRefundPayment({ open: true, payment });
    }

    // Copied at click time, not read back by id, so a refetch while the dialog is open never
    // changes what `expected` sends.
    function openEditSessionFee(sessionCharge: SessionCharge) {
        setEditSessionFee({ open: true, sessionCharge });
    }

    return (
        <SheetPanel
            type="clients/billing"
            side="right"
            size="medium"
            variant="chart"
            showCloseButton={false}
        >
            <SheetHeader variant="breadcrumbs" className="shrink-0">
                <SheetBreadcrumb
                    getLabel={(breadcrumbSheet) =>
                        breadcrumbSheet.type === 'clients/details' &&
                        breadcrumbSheet.data.id === client?.id
                            ? client.name
                            : undefined
                    }
                />
                <SheetClose asChild>
                    <SheetCloseButton className="ml-auto shrink-0" />
                </SheetClose>
            </SheetHeader>
            <div className="min-h-0 flex flex-col flex-1 overflow-y-auto">
                {isBillingLoading ? (
                    <>
                        <SheetTitle className="sr-only">Billing</SheetTitle>
                        <SheetDescription className="sr-only">
                            Billing for {client?.name}.
                        </SheetDescription>
                        <CenteredSpinner label="Loading billing" />
                    </>
                ) : (
                    <>
                        <SheetHeader variant="chart" bordered>
                            <SheetTitle>Billing</SheetTitle>
                            <SheetDescription className="sr-only">
                                Billing for {client?.name}.
                            </SheetDescription>
                            <ClientBillingSheetSummary balance={balanceQuery.data} />
                        </SheetHeader>
                        <Tabs
                            defaultValue="sessions"
                            className="flex flex-col"
                            onValueChange={(value) => {
                                if (value === 'activity') setHasOpenedActivity(true);
                            }}
                        >
                            <div className="flex flex-col px-[var(--sheet-gutter)] py-6">
                                <TabsList className="w-full max-w-95 self-center [&>*]:flex-1">
                                    <TabsTrigger value="sessions">Sessions</TabsTrigger>
                                    <TabsTrigger value="activity">Activity</TabsTrigger>
                                    <TabsTrigger value="documents">Documents</TabsTrigger>
                                    <TabsTrigger value="settings">Settings</TabsTrigger>
                                </TabsList>
                            </div>
                            <TabsContent
                                value="sessions"
                                className="mt-0 px-[var(--sheet-gutter)] py-4"
                            >
                                <ClientBillingSheetSessions
                                    client={client}
                                    pages={sessionChargeListQuery.data?.pages}
                                    unpaidCount={balanceQuery.data?.unpaidCount}
                                    paidCount={balanceQuery.data?.paidCount}
                                    hasNextPage={sessionChargeListQuery.hasNextPage}
                                    isFetchingNextPage={sessionChargeListQuery.isFetchingNextPage}
                                    isFetchNextPageError={
                                        sessionChargeListQuery.isFetchNextPageError
                                    }
                                    fetchNextPage={sessionChargeListQuery.fetchNextPage}
                                    onAddPayment={openRecordPayment}
                                    onOpenPayment={openAppliedToFromTransaction}
                                    onRefund={openRefund}
                                    onEditFee={openEditSessionFee}
                                />
                            </TabsContent>
                            <TabsContent
                                value="activity"
                                className="mt-0 px-[var(--sheet-gutter)] py-4"
                            >
                                <ClientBillingSheetActivity
                                    pages={isWriteOffsFilter ? [] : paymentListQuery.data?.pages}
                                    isError={!isWriteOffsFilter && paymentListQuery.isError}
                                    hasNextPage={!isWriteOffsFilter && paymentListQuery.hasNextPage}
                                    isFetchingNextPage={
                                        !isWriteOffsFilter && paymentListQuery.isFetchingNextPage
                                    }
                                    isFetchNextPageError={
                                        !isWriteOffsFilter && paymentListQuery.isFetchNextPageError
                                    }
                                    fetchNextPage={paymentListQuery.fetchNextPage}
                                    unpaidCount={balanceQuery.data?.unpaidCount ?? 0}
                                    filter={activityFilter}
                                    onFilterChange={setActivityFilter}
                                    getServiceLabel={getServiceLabel}
                                    onAddPayment={openRecordPayment}
                                    onOpenAppliedTo={openAppliedToFromActivity}
                                    onOpenOriginalPayment={openAppliedToFromRefund}
                                    onRefund={openRefund}
                                />
                            </TabsContent>
                            <TabsContent
                                value="documents"
                                className="mt-0 px-[var(--sheet-gutter)] pt-6 pb-8"
                            >
                                <ClientBillingSheetDocuments />
                            </TabsContent>
                            <TabsContent
                                value="settings"
                                className="mt-0 px-[var(--sheet-gutter)] py-4"
                            >
                                <ClientBillingSheetSettings />
                            </TabsContent>
                        </Tabs>
                    </>
                )}
            </div>
            {appliedTo && (
                <ClientBillingSheetAppliedToDialog
                    payment={appliedTo.payment}
                    highlightedSessionChargeId={appliedTo.highlightedSessionChargeId}
                    getServiceLabel={getServiceLabel}
                    onClose={() => setAppliedTo(null)}
                />
            )}
            {client && (
                <RecordPaymentSheet
                    clientId={client.id as ClientId}
                    clientName={client.name}
                    open={recordPayment.open}
                    onOpenChange={(open) => setRecordPayment((current) => ({ ...current, open }))}
                    selectedSessionChargeId={recordPayment.selectedSessionChargeId}
                />
            )}
            {client && (
                <RefundPaymentSheet
                    clientId={client.id as ClientId}
                    clientName={client.name}
                    payment={refundPayment.payment}
                    open={refundPayment.open}
                    onOpenChange={(open) => setRefundPayment((current) => ({ ...current, open }))}
                />
            )}
            {client && (
                <EditSessionFeeDialog
                    clientId={client.id as ClientId}
                    sessionCharge={editSessionFee.sessionCharge}
                    open={editSessionFee.open}
                    onOpenChange={(open) => setEditSessionFee((current) => ({ ...current, open }))}
                />
            )}
        </SheetPanel>
    );
}
